<template>
<b-container class="mt-5">
  <b-form>
    <JobOptions v-model="job" />

    <b-form-group label="Images:" label-cols="2" label-align="right">
      <b-form-file
        class="form-control"
        id="files"
        type="file"
        multiple
        accept="image"
        :file-name-formatter="formatNames"
        placeholder="Choose images or drop them here..."
        drop-placeholder="Drop images here..."
        @change="onFilesChange"
      />
    </b-form-group>

    <b-collapse v-model="hasImages">

      <MaskMaker
        v-model="job.maskArgs"
        v-if="hasImages"
        :image="this.selected_files.length ? this.selected_files[0].file : this.all_files[0].file"
      />

      <b-form-group>
        <b-card align="left">
          <template v-slot:header>
            <h4 class="mb-0 float-left">Images</h4>
            <b-button
              variant="outline-danger"
              class="float-right"
              :disabled="!selected_files.length"
              @click="deleteImgs"
              size="sm"
            >Delete selected</b-button>
          </template>

          <preview-area
            :files="this.all_files"
            v-model="selected_files"
            class="overflow-auto"
            style="max-height:35vh"
          >
            <template v-slot:preview="{file:f}">
              <img-preview :file="f" />
            </template>
          </preview-area>
        </b-card>
      </b-form-group>

      <b-card header="Image preprocessing" class="mb-4">
        <b-form-group
          label="Quality:"
          description="Lower number reduces upload size, but can cause small artifacts in model"
          label-cols="2"
          label-align="right"
        >
          <b-form-input type="range" v-model="preProcess.quality" min="50" max="100" step="5" />
          {{preProcess.quality}}
          {{preProcess.quality == 75 ? ' (default)' : ''}}
        </b-form-group>
        <b-form-group
          label="Resolution:"
          description="Lower number significantly reduces upload size and processing time, but reduces fidelity of model"
          label-cols="2"
          label-align="right"
        >
          <b-form-input type="range" v-model="preProcess.resize" min="0" max="2" />
          {{["25%", "50%", "100% (default)"][preProcess.resize]}}
        </b-form-group>
      </b-card>
    </b-collapse>
    <b-form-group>
      <b-button
        variant="primary"
        class="mr-1"
        v-if="(status !== s.LOADING && all_files.length)"
        @click="zip"
      >Upload {{all_files.length}} images (~ {{estimatedBytes | niceBytes}})</b-button>
      <b-button v-if="status == s.LOADING" disabled class="mr-1">
        {{uploadStatusText}}
        <b-spinner small class="ml-1" style="margin-bottom:0.12em" />
      </b-button>

    </b-form-group>
  </b-form>
</b-container>
</template>

<script>
import PreviewArea from "@/components/PreviewArea";
import ImgPreview from "@/components/ImgPreview";
import JobOptions from "@/components/JobOptions";
import MaskMaker from "@/components/MaskMaker";

import EXIF from "exif-js";
import JSZip from "jszip";

import * as bs from 'bootstrap-vue';
import Vue from 'vue';

[
    bs.FormGroupPlugin,
    bs.FormInputPlugin,
    bs.CardPlugin,
    bs.SpinnerPlugin,
    bs.ButtonPlugin,
    bs.ButtonGroupPlugin,
    bs.FormPlugin,
    bs.FormFilePlugin,
].forEach(p=> Vue.use(p))

export default {
  components: { PreviewArea, ImgPreview, JobOptions, MaskMaker },
  beforeRouteEnter (t, f, next){ next(vm => vm.$api.is('logged_in') ? next() : (location.href = vm.$api.login()))},
  data() {
    return {
      all_files: [],
      selected_files: [],
      job: { name: null, options: { quality: "medium" }, maskArgs: "" },
      s: { WAITING: 0, LOADING: 1, DONE: 2 },
      status: 0,
      uploadStatusText: null,
      orginalBytes: 0,
      preProcess: { quality: 75, resize: 2 }
    };
  },
  filters: {
    niceBytes(x) {
      const units = ["bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
      let l = 0,
        n = parseInt(x, 10) || 0;
      while (n >= 1024 && ++l) n = n / 1024;
      return n.toFixed(n < 10 && l > 0 ? 1 : 0) + " " + units[l];
    }
  },
  computed: {
    estimatedBytes() {
      let resizeMult = [1.3 / 16, 1.3 / 4, 1][this.preProcess.resize];
      let qualityMult = this.preProcess.quality / 100;
      return this.orginalBytes * resizeMult * qualityMult;
    },
    hasImages: {
      get() {
        return this.all_files.length > 0;
      },
      set() {}
    }
  },
  methods: {
    addFile(file) {
      this.status = this.s.LOADING;

      let new_files = [...this.all_files, file];
      this.all_files = new_files.sort((a, b) => {
        if (a.file.name < b.file.name) return -1;
        if (a.file.name > b.file.name) return 1;
        return 0;
      });

      this.status = this.s.WAITING;
    },
    formatNames(files) {
      return files.length === 1
        ? files[0].name
        : `${files.length} files selected`;
    },
    deleteImgs() {
      this.all_files = this.all_files.filter(
        f => !this.selected_files.includes(f)
      );
    },
    async preProcessImages(files, quality, resize) {
      let size = {};
      this.uploadStatusText = "Preparing images";
      //Uploaded files to array buffers
      let filedata = await Promise.all(files.map(f => f.file.arrayBuffer()));

      size.original = filedata
        .map(i => i.byteLength)
        .reduce((v, t) => v + t, 0);

      //Rename files
      let images = filedata.map((content, i) => {
        const name = `${i}.${files[i].file.name.match(/.*\.([A-z0-9]*$)/)[1]}`;
        return { name, content };
      });

      //Create 1 worker for each core. Distribute jobs evenly
      const command = `mogrify -quality ${quality} -resize ${resize}% -format jpg *`.split(
        " "
      );
      let chunksize = Math.ceil(images.length / navigator.hardwareConcurrency);
      let workers = [];
      while (images.length) {
        //Magick is loaded as module via script tag in index.html
        // eslint-disable-next-line
        workers.push(Magick.Call(images.splice(0, chunksize), command));
      }

      this.uploadStatusText = "Compressing images";

      //Wait for jobs to finish (~1sec per image @ 6k*3k w/ 4 cores)
      let compressedFiles = await Promise.all(workers);

      //Ignore the originals - all files now .jpg
      compressedFiles = compressedFiles
        .flat()
        .filter(f => f.name.match(/[0-9]*\.jpg$/));

      size.compressed = compressedFiles
        .map(i => i.buffer.length)
        .reduce((v, t) => v + t, 0);
      return compressedFiles;
    },
    async zip() {
      this.status = this.s.LOADING;
      let resize = [25, 50, 100][this.preProcess.resize];

      let compressedFiles = await this.preProcessImages(
        this.all_files,
        this.preProcess.quality,
        resize
      );

      //Zip images
      this.uploadStatusText = "Zipping images";
      let archive = new JSZip();
      compressedFiles.forEach(f => archive.file(f.name, f.buffer));
      let zip_blob = await archive.generateAsync({
        type: "blob",
        compression: "STORE"
      });

      //Upload
      let body = new FormData();
      body.append("imgsZip", zip_blob);
      body.append("name", this.job.name);
      body.append("optionsJSON", JSON.stringify(this.job));
      this.uploadStatusText = "Uploading images";
      await this.$api.upload(body);
      this.status = this.s.DONE;
      this.$router.push("process");
    },
    onFilesChange(e) {
      let files = e.target.files || e.dataTransfer.files;
      if (files) {
        files = Array.from(files);
        this.orginalBytes = files.map(f => f.size).reduce((v, t) => v + t, 0);
        files.forEach(async file => {
          //Use EXIF thumbnail if available
          let exif = EXIF.readFromBinaryFile(await file.arrayBuffer());
          if (exif && exif.thumbnail && exif.thumbnail.blob) {
            const reader = new FileReader();
            reader.onloadend = () => {
              this.addFile({ file, preview: reader.result });
            };
            reader.readAsDataURL(exif.thumbnail.blob);
          } else this.addFile({ file });
        });
      }
    }
  }
};
</script>

<style>
.card-img-right {
  object-fit: cover;
  width: 10rem;
}
</style>